/**
 * The Enterprise rate card and deal arithmetic — the same rules as the API's
 * src/vendor-plans/pricing/rate-card.ts (docs/pricing-v2.md). The calculator
 * loads the card from `GET plans/rate-card`; this copy is what it shows while
 * that loads, and what the tests price against.
 */

export interface RateCardItem {
  key: string;
  label: string;
  unit: string;
  unitPrice: number;
  adds?: Record<string, number>;
  base?: boolean;
  oneTime?: boolean;
}

export interface RateCard {
  currency: string;
  minTermMonths: number;
  termDiscounts: { termMonths: number; percent: number }[];
  maxSalesDiscountPercent: number;
  baseLimits: Record<string, number>;
  items: RateCardItem[];
}

export const DEFAULT_RATE_CARD: RateCard = {
  currency: 'usd',
  minTermMonths: 12,
  termDiscounts: [
    { termMonths: 12, percent: 0 },
    { termMonths: 24, percent: 8 },
    { termMonths: 36, percent: 15 },
  ],
  maxSalesDiscountPercent: 15,
  baseLimits: { maxProducts: 5000, maxRoomAnalysesPerMonth: 10000, monthlyCredits: 500, maxShopperChatsPerMonth: 2000, maxSeats: 10 },
  items: [
    { key: 'platform', label: 'Enterprise platform', unit: '1 website, 5,000 products, 10,000 rooms/mo, 500 credits/mo, 2,000 chats/mo, 10 seats', unitPrice: 2000, base: true },
    { key: 'website', label: 'Additional website, brand or country store', unit: 'each', unitPrice: 400 },
    { key: 'products', label: 'Products', unit: '+5,000', unitPrice: 200, adds: { maxProducts: 5000 } },
    { key: 'rooms', label: 'Rooms analysed', unit: '+10,000 / month', unitPrice: 300, adds: { maxRoomAnalysesPerMonth: 10000 } },
    { key: 'credits', label: 'Content Studio credits', unit: '+1,000 / month', unitPrice: 250, adds: { monthlyCredits: 1000 } },
    { key: 'chats', label: 'AI shopper assistant chats', unit: '+5,000 / month', unitPrice: 250, adds: { maxShopperChatsPerMonth: 5000 } },
    { key: 'success', label: 'Success manager and SLA', unit: '99.9% uptime, 4 h response', unitPrice: 1000 },
    { key: 'integration', label: 'Integration and onboarding', unit: 'one time, from', unitPrice: 3000, oneTime: true },
  ],
};

export interface DealLineInput {
  key: string;
  qty: number;
  unitPrice?: number;
}

export interface PricedDeal {
  lines: { key: string; label: string; qty: number; unitPrice: number; total: number; oneTime: boolean }[];
  listMonthly: number;
  termDiscountPercent: number;
  salesDiscountPercent: number;
  monthlyAmount: number;
  oneTimeTotal: number;
  contractValue: number;
  limits: Record<string, number>;
  belowFloor: boolean;
  discountWarning: boolean;
}

export function priceDeal(
  input: { lines: DealLineInput[]; termMonths: number; salesDiscountPercent?: number },
  card: RateCard = DEFAULT_RATE_CARD,
): PricedDeal {
  const byKey = new Map(card.items.map((i) => [i.key, i]));
  const lines: PricedDeal['lines'] = [];
  const limits: Record<string, number> = { ...card.baseLimits };
  const base = card.items.find((i) => i.base);
  const wanted = new Map<string, DealLineInput>();
  for (const l of input.lines || []) if (byKey.has(l.key)) wanted.set(l.key, l);
  if (base && !wanted.has(base.key)) wanted.set(base.key, { key: base.key, qty: 1 });

  for (const item of card.items) {
    const l = wanted.get(item.key);
    if (!l) continue;
    const qty = item.base ? 1 : Math.max(0, Math.floor(Number(l.qty) || 0));
    if (qty === 0) continue;
    const unitPrice = typeof l.unitPrice === 'number' && l.unitPrice >= 0 ? l.unitPrice : item.unitPrice;
    lines.push({ key: item.key, label: item.label, qty, unitPrice, total: qty * unitPrice, oneTime: !!item.oneTime });
    for (const [k, v] of Object.entries(item.adds || {})) limits[k] = (limits[k] ?? 0) + v * qty;
  }

  const termMonths = Math.max(card.minTermMonths, Math.floor(Number(input.termMonths) || card.minTermMonths));
  const termDiscountPercent =
    [...card.termDiscounts].sort((a, b) => b.termMonths - a.termMonths).find((t) => termMonths >= t.termMonths)?.percent ?? 0;
  const salesDiscountPercent = Math.max(0, Math.min(100, Number(input.salesDiscountPercent) || 0));
  const listMonthly = lines.filter((l) => !l.oneTime).reduce((s, l) => s + l.total, 0);
  const oneTimeTotal = lines.filter((l) => l.oneTime).reduce((s, l) => s + l.total, 0);
  const monthlyAmount = Math.round(listMonthly * (1 - termDiscountPercent / 100) * (1 - salesDiscountPercent / 100));
  return {
    lines,
    listMonthly,
    termDiscountPercent,
    salesDiscountPercent,
    monthlyAmount,
    oneTimeTotal,
    contractValue: monthlyAmount * termMonths + oneTimeTotal,
    limits,
    belowFloor: monthlyAmount < (base?.unitPrice ?? 0) * (1 - card.maxSalesDiscountPercent / 100),
    discountWarning: salesDiscountPercent > card.maxSalesDiscountPercent,
  };
}

export const money = (amount: number, currency = 'usd'): string => {
  const c = currency.toLowerCase();
  const sym = c === 'usd' ? '$' : c === 'eur' ? '€' : c === 'gbp' ? '£' : `${c.toUpperCase()} `;
  return `${sym}${String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};
